import React, { useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import clsx from "clsx";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from "@material-ui/core";
import FolderOpenIcon from "@material-ui/icons/FolderOpen";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import LockIcon from "@material-ui/icons/Lock";
import moment from "moment";
import { getPatientCasesRequest } from "../../CasesApiAction";
import { getUserInfo } from "../../../../utilities/storage";
import { showSnackbar } from "../../../../utilities/UtilitiesActions";
import { useStyles } from "./styles";

/**
 * function to sort cases with the most recently created one first
 * @param rows: cases list
 * @returns {*[]}
 */
const sortLatestFirst = (rows) => {
  return [...rows].sort((a, b) => {
    const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    if (bTime !== aTime) return bTime - aTime;
    return b.id - a.id;
  });
};

/**
 * function to render the patient cases picker dialog
 * @param open: whether the dialog is open
 * @param onClose: function to close the dialog
 * @param onSelect: function called with the selected case
 * @param patientId: patient whose cases are listed (used when cases are not passed in)
 * @param cases: already fetched cases list, skips the api call when provided
 * @param patientName: patient name shown as the dialog subtitle
 * @param currentCaseId: case highlighted in the list
 * @param currentLabel: label of the chip shown on the highlighted case
 * @param selectedDept: selected department, used for the case access check
 * @param getPatientCases: function to get cases of a patient
 * @returns {JSX.Element}
 * @constructor
 */
const PatientCasesDialog = ({
  open,
  onClose,
  onSelect,
  patientId,
  cases,
  patientName,
  currentCaseId,
  currentLabel = "Current",
  selectedDept,
  getPatientCases,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [caseRows, setCaseRows] = useState([]);

  useEffect(() => {
    if (!open) return;

    if (cases) {
      setCaseRows(sortLatestFirst(cases));
      return;
    }

    if (!patientId) {
      setCaseRows([]);
      return;
    }

    getPatientCases(patientId).then(
      (res) => setCaseRows(sortLatestFirst(res?.rows || [])),
      () => setCaseRows([])
    );
  }, [open, cases, patientId, getPatientCases]);

  /**
   * function to check whether the logged in doctor can open the case
   * @param caseItem: case item details
   * @returns {boolean}
   */
  const hasAccess = (caseItem) => {
    if (!caseItem.Caregivers) return true;
    if (!selectedDept || selectedDept.headDoctor) return true;
    return caseItem.Caregivers.some((doctor) => doctor.id === getUserInfo().id);
  };

  const handleCaseClick = (caseItem) => {
    if (!hasAccess(caseItem)) {
      dispatch(
        showSnackbar(
          "You do not have permission to view this case. Contact a case doctor to request permission.",
          "error"
        )
      );
      return;
    }
    onSelect(caseItem);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <Box className={classes.dialogHeader}>
        <Box className={classes.dialogHeaderLeft}>
          <Box className={classes.dialogHeaderIcon}>
            <FolderOpenIcon style={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography className={classes.dialogTitleText}>
              Select a Case
            </Typography>
            <Typography className={classes.dialogSubtitle}>
              {patientName
                ? `${patientName} · ${caseRows.length} case${
                    caseRows.length === 1 ? "" : "s"
                  }`
                : `${caseRows.length} case${caseRows.length === 1 ? "" : "s"}`}
            </Typography>
          </Box>
        </Box>
      </Box>

      <DialogContent className={classes.dialogBody}>
        {caseRows.length === 0 ? (
          <Typography className={classes.noCasesText}>
            No cases available for this patient
          </Typography>
        ) : (
          caseRows.map((caseItem) => {
            const locked = !hasAccess(caseItem);
            return (
              <Box
                key={caseItem.id}
                className={clsx(
                  classes.caseCard,
                  caseItem.id === currentCaseId && classes.caseCardCurrent,
                  locked && classes.caseCardLocked
                )}
                onClick={() => handleCaseClick(caseItem)}
              >
                <Box className={classes.caseCardLeft}>
                  <Box className={classes.caseCardTopRow}>
                    <Typography className={classes.caseNumber}>
                      Case #{caseItem.id}
                    </Typography>
                    {caseItem.priority === 1 && (
                      <Typography
                        className={clsx(classes.caseChip, classes.criticalChip)}
                      >
                        Urgent
                      </Typography>
                    )}
                    {caseItem.id === currentCaseId && (
                      <Typography className={classes.caseChip}>
                        {currentLabel}
                      </Typography>
                    )}
                  </Box>

                  <Typography noWrap className={classes.caseDesc}>
                    {caseItem.desc || "-"}
                  </Typography>

                  <Typography className={classes.caseMeta}>
                    Created
                    {caseItem.createdByFirstName
                      ? ` by ${caseItem.createdByFirstName} ${
                          caseItem.createdByLastName || ""
                        }`.trimEnd()
                      : ""}
                    {caseItem.createdAt
                      ? `, ${moment(caseItem.createdAt).format(
                          "DD MMM YYYY, hh:mm a"
                        )}`
                      : ""}
                  </Typography>

                  {caseItem.updatedAt && (
                    <Typography className={classes.caseMeta}>
                      Updated on{" "}
                      {moment(caseItem.updatedAt).format("DD MMM YYYY, hh:mm a")}
                    </Typography>
                  )}
                </Box>

                <Box className={classes.caseCardRight}>
                  {locked ? (
                    <LockIcon className={classes.lockIcon} />
                  ) : (
                    <ChevronRightIcon />
                  )}
                </Box>
              </Box>
            );
          })
        )}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button onClick={onClose} className={classes.cancelBtn}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = (state) => {
  return {
    selectedDept: state.ayucareUsers.selectedDept,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getPatientCases: (id) => {
      return dispatch(getPatientCasesRequest(id)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(PatientCasesDialog);
